import type {
	AbstractResource,
	CustomDataType,
	Lever,
	ResourceSchema,
	ResourceSchemaColumn
} from '@semanticedge/persistence-service-noheaders-client/src'
import {
	ResourceType,
	CustomTypeEnum
} from '@semanticedge/persistence-service-noheaders-client/src'
import {
	REFERENCE_TABLE_CLASS_NAME,
	type OpenApiTypedValue,
	type OutgoingCustomDataType,
	type ReferenceTable,
	type ReferenceTableRow
} from '@repo/commons/models'

const isRecord = (value: unknown): value is Record<string, unknown> =>
	!!value && typeof value === 'object' && !Array.isArray(value)

const getPersistenceClass = (resource: object): string | undefined => {
	const persistenceClass: unknown = '@class' in resource ? resource['@class'] : undefined

	return typeof persistenceClass === 'string' ? persistenceClass : undefined
}

export const isReferenceTable = (resource: AbstractResource): resource is ReferenceTable =>
	resource.resourceType === ResourceType.ReferenceTable ||
	resource._class === REFERENCE_TABLE_CLASS_NAME ||
	!!getPersistenceClass(resource)?.includes(REFERENCE_TABLE_CLASS_NAME)

export const isLeverResource = (resource: AbstractResource): resource is Lever => {
	if (isReferenceTable(resource)) return false

	return (
		resource.resourceType === ResourceType.Lever ||
		resource._class === 'Lever' ||
		!!getPersistenceClass(resource)?.includes('Lever')
	)
}

const CUSTOM_DATA_TYPE_CLASS = '.CustomDataType' as const

const COLUMN_TYPE_TO_OPEN_API_CLASS = {
	[CustomTypeEnum.Boolean]: '.CustomBooleanType',
	[CustomTypeEnum.Integer]: '.CustomIntegerType',
	[CustomTypeEnum.String]: '.CustomStringType',
	[CustomTypeEnum.Float]: '.CustomFloatType',
	[CustomTypeEnum.Custom]: '.CustomDataType',
	[CustomTypeEnum.IntegerList]: '.CustomIntegerListType',
	[CustomTypeEnum.FloatList]: '.CustomFloatListType',
	[CustomTypeEnum.StringList]: '.CustomStringListType',
	[CustomTypeEnum.BooleanList]: '.CustomBooleanListType',
	[CustomTypeEnum.CdtList]: '.CustomCDTListType'
} as const satisfies Record<CustomTypeEnum, string>

const CUSTOM_DATA_TYPE_META_KEYS = new Set(['_class', '@class', 'empty', 'value'])

const toOpenApiTypedValue = (value: unknown, openApiClass: string): OpenApiTypedValue => ({
	'@class': openApiClass,
	value
})

const LIST_ITEM_CLASS: Partial<Record<CustomTypeEnum, string>> = {
	[CustomTypeEnum.IntegerList]: '.CustomIntegerType',
	[CustomTypeEnum.FloatList]: '.CustomFloatType',
	[CustomTypeEnum.StringList]: '.CustomStringType',
	[CustomTypeEnum.BooleanList]: '.CustomBooleanType',
	[CustomTypeEnum.CdtList]: '.CustomDataType'
}

const wrapListItems = (items: unknown[], columnType: CustomTypeEnum): unknown[] => {
	const itemClass = LIST_ITEM_CLASS[columnType]
	if (!itemClass) return items

	return items.map((item) => toOpenApiTypedValue(item, itemClass))
}

const unwrapWireValue = (entry: unknown): unknown => {
	if (entry == null) return entry
	if (Array.isArray(entry)) return entry.map(unwrapWireValue)
	if (typeof entry === 'object' && 'value' in entry) {
		return unwrapWireValue(entry.value)
	}

	return entry
}

const unwrapCellValue = (entry: unknown): string | number | boolean | null => {
	const unwrapped = unwrapWireValue(entry)
	if (unwrapped == null) return null
	if (
		typeof unwrapped === 'string' ||
		typeof unwrapped === 'number' ||
		typeof unwrapped === 'boolean'
	) {
		return unwrapped
	}
	if (Array.isArray(unwrapped)) return JSON.stringify(unwrapped)
	return null
}

type WireCellCoercion = (raw: string | number | boolean) => unknown

const toNumberOrRaw = (
	raw: string | number | boolean,
	parse: (value: string) => number
): number | string | boolean => {
	const parsed = typeof raw === 'number' ? raw : parse(String(raw))

	return Number.isNaN(parsed) ? raw : parsed
}

const COERCE_SCALAR_CELL: Partial<Record<CustomTypeEnum, WireCellCoercion>> = {
	[CustomTypeEnum.Boolean]: (raw) => raw === true || raw === 'true',
	[CustomTypeEnum.Integer]: (raw) => toNumberOrRaw(raw, (value) => Number.parseInt(value, 10)),
	[CustomTypeEnum.Float]: (raw) => toNumberOrRaw(raw, Number.parseFloat)
}

const parseJsonArray = (raw: string | number | boolean): unknown[] | null => {
	if (Array.isArray(raw)) return raw
	if (typeof raw !== 'string') return null

	try {
		const parsed: unknown = JSON.parse(raw)

		return Array.isArray(parsed) ? parsed : null
	} catch {
		return null
	}
}

const isListColumnType = (columnType: CustomTypeEnum): boolean => columnType in LIST_ITEM_CLASS

const coerceWireCellValue = (
	raw: string | number | boolean | null,
	columnType: CustomTypeEnum | undefined
): unknown => {
	if (raw == null) return null
	if (!columnType) return raw

	const coerceScalar = COERCE_SCALAR_CELL[columnType]
	if (coerceScalar) return coerceScalar(raw)

	if (!isListColumnType(columnType)) return raw

	const items = parseJsonArray(raw)

	return items ? wrapListItems(items, columnType) : raw
}

const isMetaKey = (key: string): boolean => CUSTOM_DATA_TYPE_META_KEYS.has(key)

const getWrappedCells = (entry: Record<string, unknown>): Record<string, unknown> | null => {
	const hasCellsAtTopLevel = Object.keys(entry).some((key) => !isMetaKey(key))

	return !hasCellsAtTopLevel && isRecord(entry.value) ? entry.value : null
}

export const customDataTypeToRow = (entry: Record<string, unknown>): ReferenceTableRow => {
	const cells = getWrappedCells(entry ?? {}) ?? entry ?? {}

	return Object.fromEntries(
		Object.entries(cells)
			.filter(([key]) => !isMetaKey(key))
			.map(([key, value]) => [key, unwrapCellValue(value)])
	)
}

type RowToCustomDataTypeOptions = {
	columns?: Record<string, ResourceSchemaColumn> | null
}

const isWireCustomDataType = (row: ReferenceTableRow | CustomDataType): row is CustomDataType => {
	if (!isRecord(row)) return false

	const className = row['@class'] ?? row._class

	return typeof className === 'string' && className.includes('CustomDataType')
}

export const rowToCustomDataType = (
	row: ReferenceTableRow | CustomDataType,
	{ columns }: RowToCustomDataTypeOptions = {}
): OutgoingCustomDataType => {
	const flatRow = isWireCustomDataType(row) ? customDataTypeToRow(row) : row
	const columnNames = Object.keys(columns ?? flatRow)

	const result: OutgoingCustomDataType = { '@class': CUSTOM_DATA_TYPE_CLASS }

	columnNames.forEach((columnName) => {
		const columnType = columns?.[columnName]?.type
		const openApiClass =
			(columnType && COLUMN_TYPE_TO_OPEN_API_CLASS[columnType]) || '.CustomStringType'
		const raw = flatRow[columnName] ?? null

		result[columnName] = toOpenApiTypedValue(coerceWireCellValue(raw, columnType), openApiClass)
	})

	return result
}

export const renameReferenceTableRowColumns = (
	rows: readonly ReferenceTableRow[],
	renames: Record<string, string>
): ReferenceTableRow[] =>
	rows.map((row) =>
		Object.fromEntries(
			Object.entries(row).map(([columnName, value]) => [renames[columnName] ?? columnName, value])
		)
	)

export const findSchemaById = (
	schemas: readonly ResourceSchema[] | undefined | null,
	schemaId: string | undefined
): ResourceSchema | undefined => (schemaId ? schemas?.find(({ id }) => id === schemaId) : undefined)

export const getSchemaColumnsForTable = (
	table: ReferenceTable,
	schemas: readonly ResourceSchema[] | undefined | null
): Record<string, ResourceSchemaColumn> | undefined =>
	findSchemaById(schemas, table.resourceSchemaId)?.columns
